import { useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useScroll } from "react-use";

import { Stack, StackProps, styled, useScrollTrigger } from "@mui/material";

import { useIsExternalPage } from "@/hooks/useIsExternalPage";
import { useAuth } from "@/providers";

import { Feet } from "./Feet";
import { Head } from "./Head";
import { Mask } from "./Mask";

interface LayoutProps extends StackProps {
  suppressNavElements?: boolean;
}

export const layoutClasses = {
  root: "layout-root",
  head: "layout-head",
  body: "layout-body",
  feet: "layout-feet",
  stack: "layout-stack",
};

const StyledRoot = styled(Stack)(({ theme }) => ({
  background: theme.palette.background.default,
  [`&, & .${layoutClasses.stack}`]: {
    flex: "1 1 100%",
    minHeight: "100%",
    overflowX: "hidden",
  },
}));

export const Layout: React.FC<LayoutProps> = ({
  suppressNavElements,
  ...props
}) => {
  const isExternalPage = useIsExternalPage();
  const { pathname } = useLocation();
  const { user } = useAuth();

  // Reference to the #root element (the actual DOM root, not our Layout component)
  // Use useMemo to ensure we only query the DOM once
  const scrollTarget = useMemo(() => document.getElementById("root"), []);
  const rootElementRef = useRef<HTMLElement | null>(scrollTarget);

  const trigger = useScrollTrigger({
    target: scrollTarget || undefined,
  });

  const { y: yScrollOffset } = useScroll(rootElementRef);

  const hideHead = isExternalPage && trigger;
  const hasScrolled = yScrollOffset > 10;

  useEffect(() => {
    scrollTarget?.scrollTo(0, 0);
  }, [scrollTarget, pathname]);

  return (
    <StyledRoot className={layoutClasses.root} {...props}>
      {!suppressNavElements && (
        <Head
          color={!user && hasScrolled ? "inherit" : "transparent"}
          elevation={!user && hasScrolled ? 1 : 0}
          sx={{ transform: !user && hideHead ? "translateY(-100%)" : "none" }}
        />
      )}
      <Stack sx={{ minHeight: "100%", flexGrow: 1 }}>
        <Outlet />
      </Stack>
      <Feet suppressNavElements={suppressNavElements} />
      <Mask />
    </StyledRoot>
  );
};
